import { useState } from 'react';
import { Trans, useLingui } from '@lingui/react/macro';

import Button from '@/design_system/Button';
import { Footer, FooterButtons, FooterError } from '@/design_system/Footer';
import Paragraph from '@/design_system/Typography/Paragraph';
import IconCancel from '@/icons/Cancel.svg';
import { useCompleteQualityControlServiceAdjustment } from '@/models/article';
import { useArticleContext } from '@/routes/Requests/contexts/ArticleContext';
import { useRequestContext } from '@/routes/Requests/contexts/RequestContext';

import { ArticleActionProps } from './ArticleActions';

export const AdjustServiceDuringQualityControlAction = ({
  article,
  onActionDone,
}: ArticleActionProps) => {
  const { t } = useLingui();
  const { view: requestView } = useRequestContext();
  const { state, view, hasError } = useArticleContext();

  const [apiError, setApiError] = useState<string | null>(null);

  const { mutateAsync: completeForConfirm, isPending: isPendingConfirm } =
    useCompleteQualityControlServiceAdjustment({ articleId: article.id });

  const { mutateAsync: completeForCancel, isPending: isPendingCancel } =
    useCompleteQualityControlServiceAdjustment({ articleId: article.id });

  const handleApiError = async (mutateAsync: () => Promise<void>) => {
    try {
      await mutateAsync();

      onActionDone();
    } catch (err: any) {
      console.error(err);
      setApiError(
        (err.message as string) ?? t({ id: '_general.error.unknown', message: 'Unknown error' })
      );
    }
  };

  const handleConfirm = async () => {
    setApiError(null);

    const { hasError } = state.errors.check({
      defects: view.defects.shown
        ? {
            defects: {
              missingDescription: view.defects.fields.description.editable,
              missingPhotos:
                view.defects.fields.photo.shown &&
                view.defects.fields.photo.editable &&
                view.defects.fields.photo.required,
              missingContext:
                view.defects.fields.context.shown &&
                view.defects.fields.context.editable &&
                view.defects.fields.context.required,
              missingActions:
                view.defects.fields.toBeRepaired.shown &&
                view.defects.fields.toBeRepaired.editable &&
                view.services.actions.shown,
              missingExceptionalCoverageReason:
                view.defects.fields.brandResponsibility.exceptionalCoverage.enabled &&
                view.defects.fields.brandResponsibility.exceptionalCoverage.toggle.editable,
            },
            noDefects: view.defects.required,
          }
        : undefined,
      services: {
        actions: view.services.actions.shown
          ? {
              actions: {
                missingDescription: view.services.actions.fields.description.editable,
                missingPhotos:
                  view.services.actions.fields.photo.shown &&
                  view.services.actions.fields.photo.editable &&
                  view.services.actions.fields.photo.required,
                missingRefashionPhoto:
                  view.services.actions.fields.photo.shown &&
                  view.services.actions.fields.photo.editable,
                missingContext:
                  view.services.actions.fields.context.shown &&
                  view.services.actions.fields.context.editable &&
                  view.services.actions.fields.context.required,
                missingWorkshopPrice:
                  requestView.workshopPrice.shown &&
                  view.services.actions.fields.workshopPrice.editable &&
                  view.services.actions.fields.workshopPrice.required,
                missingOrganizationPrice:
                  requestView.organizationPrice.shown &&
                  view.services.actions.fields.organizationPrice.editable &&
                  view.services.actions.fields.organizationPrice.required,
                workshopPriceUpdateInProgress:
                  requestView.workshopPrice.shown &&
                  view.services.actions.fields.workshopPrice.editable,
                organizationPriceUpdateInProgress:
                  requestView.organizationPrice.shown &&
                  view.services.actions.fields.organizationPrice.editable,
                spareParts: view.services.actions.fields.spareParts.shown
                  ? {
                      quantityIsNull: view.services.actions.fields.spareParts.quantity.editable,
                      missingWorkshopPrice:
                        requestView.workshopPrice.shown &&
                        view.services.actions.fields.spareParts.workshopPrice.editable &&
                        view.services.actions.fields.spareParts.workshopPrice.required,
                      missingOrganizationPrice:
                        requestView.organizationPrice.shown &&
                        view.services.actions.fields.spareParts.organizationPrice.editable &&
                        view.services.actions.fields.spareParts.organizationPrice.required,
                      workshopPriceUpdateInProgress:
                        requestView.workshopPrice.shown &&
                        view.services.actions.fields.spareParts.workshopPrice.editable,
                      organizationPriceUpdateInProgress:
                        requestView.organizationPrice.shown &&
                        view.services.actions.fields.spareParts.organizationPrice.editable,
                    }
                  : undefined,
                additionalInfo: {
                  missingValue: view.services.actions.fields.additionalInfo.editable,
                },
              },
              noActions: view.services.actions.canAddAction,
            }
          : undefined,
      },
    });

    if (hasError) {
      return;
    }

    await handleApiError(() => completeForConfirm({ confirm: true }));
  };

  const handleCancel = async () => {
    setApiError(null);

    await handleApiError(() => completeForCancel({ confirm: false }));
  };

  return (
    <Footer>
      {(hasError || !!apiError) && (
        <FooterError>
          {hasError && (
            <Trans id="article.actions.adjust_service_during_quality_control.errors">
              Please fill the missing information in order to complete the service adjustment
            </Trans>
          )}
          {!!apiError && (
            <Paragraph weight="medium" color="danger">
              {apiError}
            </Paragraph>
          )}
        </FooterError>
      )}
      <FooterButtons>
        <Button
          variant="secondary-danger"
          size="medium"
          onPress={handleCancel}
          isLoading={isPendingCancel}
          disabled={isPendingConfirm}
        >
          <IconCancel />
          <Trans id="article.actions.adjust_service_during_quality_control.cancel">Cancel</Trans>
        </Button>
        <Button
          variant="primary"
          size="medium"
          onPress={handleConfirm}
          isLoading={isPendingConfirm}
          disabled={isPendingCancel}
        >
          <Trans id="article.actions.adjust_service_during_quality_control.confirm">Confirm</Trans>
        </Button>
      </FooterButtons>
    </Footer>
  );
};
